/**
 * Client-side helpers to load Google Identity Services and Sign in with Apple JS,
 * and to retrieve an ID token via a popup/one-tap flow.
 *
 * The backend (`/api/auth/google/`, `/api/auth/apple/`) is responsible for
 * verifying tokens; this module only obtains them.
 */

const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || '';
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== 'undefined' ? window.location.origin + '/login' : '');

const DEBUG = String(process.env.REACT_APP_DEBUG || '').toLowerCase() === 'true';

function injectScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document is not available.'));
      return;
    }
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      }
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export function isGoogleConfigured() {
  return Boolean(GOOGLE_CLIENT_ID);
}

export function isAppleConfigured() {
  return Boolean(APPLE_CLIENT_ID);
}

export async function loadGoogleIdentity() {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google OAuth client ID is not configured.');
  }
  await injectScript(GOOGLE_SCRIPT_SRC);
  if (!window.google || !window.google.accounts || !window.google.accounts.id) {
    throw new Error('Google Identity Services failed to initialize.');
  }
  return window.google.accounts.id;
}

/**
 * Sign in with Google and resolve with the ID token.
 * Uses the GIS button/credential flow via an invisible promise wrapper.
 */
export async function signInWithGoogle() {
  const gis = await loadGoogleIdentity();
  return new Promise((resolve, reject) => {
    try {
      gis.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
          if (response && response.credential) {
            resolve({ id_token: response.credential });
          } else {
            reject(new Error('No credential returned from Google.'));
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        ux_mode: 'popup',
      });
      gis.prompt((notification) => {
        if (
          notification.isNotDisplayed?.() ||
          notification.isSkippedMoment?.() ||
          notification.isDismissedMoment?.()
        ) {
          if (DEBUG) {
            // eslint-disable-next-line no-console
            console.warn('Google One Tap not displayed; user must click the button.');
          }
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Render the official Google Sign-In button into a DOM element. Returns the ID
 * token when the user completes the flow.
 */
export async function renderGoogleButton(element, { onCredential } = {}) {
  if (!element) {
    throw new Error('Target element is required to render Google button.');
  }
  const gis = await loadGoogleIdentity();
  gis.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response) => {
      if (response && response.credential && onCredential) {
        onCredential({ id_token: response.credential });
      }
    },
    ux_mode: 'popup',
  });
  gis.renderButton(element, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    width: element.clientWidth || 320,
  });
}

let appleInitialized = false;

export async function loadAppleAuth() {
  if (!APPLE_CLIENT_ID) {
    throw new Error('Apple client ID is not configured.');
  }
  await injectScript(APPLE_SCRIPT_SRC);
  if (!window.AppleID || !window.AppleID.auth) {
    throw new Error('Apple ID JS failed to initialize.');
  }
  if (!appleInitialized) {
    window.AppleID.auth.init({
      clientId: APPLE_CLIENT_ID,
      scope: 'name email',
      redirectURI: APPLE_REDIRECT_URI,
      usePopup: true,
    });
    appleInitialized = true;
  }
  return window.AppleID.auth;
}

/**
 * Sign in with Apple. Returns { id_token, name } where `name` is only
 * populated on the user's first consent.
 */
export async function signInWithApple() {
  const appleAuth = await loadAppleAuth();
  const data = await appleAuth.signIn();
  const idToken = data?.authorization?.id_token;
  if (!idToken) {
    throw new Error('No id_token returned from Apple.');
  }
  const name = data?.user?.name || null;
  return { id_token: idToken, name };
}
